<script lang="ts">
  import {formats, project} from '@/store';

  import {AvailableFormats} from '@/types/upload';
  import {Video} from '@/types/project';
  import {createEventDispatcher, onMount} from 'svelte';
  import {appEvents} from '@/services';
  import tippy from 'tippy.js';
  import {UploadSource} from '@/types/common';

  export let video: Video;
  export let index: number;
  export let activeIndex: number;
  export let isDownloading: boolean;

  let quality: string = video.quality ? video.quality : 'N/A';
  let selectedFormat: any;
  let highestAvailable: AvailableFormats;
  let toggleDropdown: boolean = false;
  let isFetchingFormats: boolean = false;
  let is720: HTMLElement;
  let is480: HTMLElement;
  let dropdownRef: HTMLElement;

  const dispatch = createEventDispatcher();

  async function fetchAvailableFormats() {
    dispatch('set-active-index', {index: index});

    if (index == activeIndex && $formats.hasData) {
      toggleDropdown = !toggleDropdown;
    } else {
      formats.set({
        items: [],
        hasData: false,
        has1080: false
      });

      isFetchingFormats = true;
      formats
        .fetchAvailableFormats(video.youtubeUrl as string, video.source)
        .then(() => {
          isFetchingFormats = false;
          highestAvailable = $formats.items[0];
          toggleDropdown = !toggleDropdown;
        })
        .catch(() => {
          isFetchingFormats = false;
        });
    }
  }

  function selectQuality(item: any) {
    if (quality !== item.label) {
      const videoId = video.id;
      quality = item.label;
      selectedFormat = item.value;
      isDownloading = true;

      project.uploadYoutubeVideo({
        url: video.youtubeUrl as string,
        language: 'english',
        format: selectedFormat,
        source: video.source as UploadSource,
        videoId,
        options: {reencode: false}
      });
    }

    toggleDropdown = !toggleDropdown;
    return null;
  }

  onMount(() => {
    appEvents.on('upload-complete', async data => {
      isDownloading = false;
    });

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  // Function to handle click outside
  function handleClickOutside(event: any) {
    if (dropdownRef && !dropdownRef.contains(event.target)) {
      toggleDropdown = false;
    }
  }

  $: displayClass =
    toggleDropdown && $formats.hasData && index == activeIndex
      ? 'w-50 flex flex-col absolute top-0 left-0 rounded-lg border-y border bg-white z-10'
      : 'hidden';

  $: {
    if (is720) {
      let is720tooltip = `Your video is less than 1080p, which may affect video quality. For best results, upload 1080p or higher${video.youtubeUrl ? '. When YouTube makes a higher quality video, you can replace it here.' : '.'}`;
      tippy(is720, {
        theme: 'ssinfo',
        content: `<div class="flex flex-col items-center justify-start">
                            <p class="inline-block align-middle font-base text-gray-800"><span class="font-bold">Note: </span>${is720tooltip}</p>
                        </div>`,
        allowHTML: true,
        placement: 'top',
        arrow: true,
        animation: 'scale'
      });
    }
    if (is480) {
      let is480tooltip = `Your video resolution is below 720p and may result in poor quality. We recommend at least 720p, with 1080p or higher for best results${video.youtubeUrl ? '. When YouTube makes a higher quality video, you can replace it here.' : '.'}`;
      tippy(is480, {
        theme: 'sswarning',
        content: `<div class="flex flex-col items-center justify-start">
                            <p class="inline-block align-middle font-base text-gray-800"><span class="font-bold">Warning: </span>${is480tooltip}</p>
                        </div>`,
        allowHTML: true,
        placement: 'top',
        arrow: true,
        animation: 'scale'
      });
    }
  }
</script>

<div class="mt-2 flex w-full justify-start gap-2">
  <span class="text-sm text-gray-500">Resolution:</span>
  <!-- Dropdown Here -->
  <div class="relative flex w-full text-sm text-gray-500">
    {#if video.source == 'youtube' && video.quality != '1080p'}
      <span
        on:click={() => fetchAvailableFormats()}
        on:keydown={() => fetchAvailableFormats()}
        class="block cursor-pointer bg-white text-gray-500 focus:outline-none focus:ring focus:ring-primary focus:ring-opacity-40 dark:bg-zinc-800 dark:text-zinc-500 dark:focus:border-blue-300"
      >
        <span>
          {quality}
          {#if (isFetchingFormats || isDownloading) && index == activeIndex}
            <i class="fa-duotone fa-solid fa-spinner-third fa-spin-pulse float-right ml-2 py-1"></i>
            {#if isDownloading && index == activeIndex}
              <span class="ml-1">Processing...</span>
            {/if}
          {:else}
            <i class="fa-solid fa-angle-down ml-2 py-1"> </i>
          {/if}
        </span>
      </span>
      <div class={displayClass} id={'resolution' + video.id} bind:this={dropdownRef}>
        {#if !$formats.has1080}
          <div
            class="disabled block cursor-pointer border-y px-4 py-3 text-gray-300 hover:bg-gray-100"
            on:click={() => dispatch('toggle-1080-dialog')}
            on:keydown={() => dispatch('toggle-1080-dialog')}
          >
            <span>1080p <i class="fa-regular fa-circle-question"></i></span>
          </div>
        {/if}
        {#each $formats.items as item}
          <div
            class="block cursor-pointer border-y px-4 py-3 hover:bg-gray-100"
            on:click={selectQuality(item)}
            on:keydown={selectQuality(item)}
          >
            <span>
              {item.label}
              {#if highestAvailable === item}
                <span class="ml-1 rounded-xl bg-primary px-2 py-1 text-xs uppercase text-white">Recommended</span>
              {/if}
            </span>
          </div>
        {/each}
      </div>
    {:else}
      {quality}
    {/if}
    {#if video.source == 'youtube' && video.quality !== '1080p'}
      <span class="ml-2 flex justify-start">
        {#if video.quality === '720p'}
          <span bind:this={is720}>
            <i class="fa-solid fa-circle-exclamation text-blue-400"></i>
          </span>
        {:else}
          <span bind:this={is480}>
            <i class="fa-solid fa-triangle-exclamation text-orange-400"></i>
          </span>
        {/if}
      </span>
    {/if}
  </div>
</div>
